import { useState } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { CONTACT_STAGES } from "@/lib/crm";
import { toast } from "sonner";

const SOURCES = ["homepage", "contact_page", "whitepaper", "referral", "event", "cold inbound"] as const;
const SITUATIONS = [
  "Litigation & Media Exposure",
  "Regulatory / Banking Pressure",
  "High-Value Dispute",
  "Other High-Stakes Situation",
  "Whitepaper Download",
] as const;
const URGENCIES = [
  "Active / in the next 48 hours",
  "Upcoming / within 2 weeks",
  "Planning ahead / no immediate deadline",
] as const;

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  organisation: z.string().trim().max(150).optional().or(z.literal("")),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  role: z.string().trim().max(100).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSource?: typeof SOURCES[number];
  lockSource?: boolean;
  onCreated?: () => void;
};

export function AddContactDialog({ open, onOpenChange, defaultSource = "contact_page", lockSource = false, onCreated }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", organisation: "", phone: "", role: "",
    source: defaultSource as typeof SOURCES[number],
    situation_type: "" as "" | typeof SITUATIONS[number],
    urgency: "" as "" | typeof URGENCIES[number],
    stage: "new" as typeof CONTACT_STAGES[number],
    message: "Manually added by admin",
  });

  const update = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm({
    name: "", email: "", organisation: "", phone: "", role: "",
    source: defaultSource, situation_type: "", urgency: "",
    stage: "new", message: "Manually added by admin",
  });

  const submit = async () => {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("contacts").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      organisation: parsed.data.organisation || null,
      phone: parsed.data.phone || null,
      role: parsed.data.role || null,
      source: form.source,
      situation_type: form.situation_type || null,
      urgency: form.urgency || null,
      stage: form.stage,
      message: parsed.data.message,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Contact added");
    reset();
    onOpenChange(false);
    onCreated?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
          <DialogDescription>Manually create a contact record.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Organisation</Label>
              <Input value={form.organisation} onChange={(e) => update("organisation", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Role</Label>
              <Input value={form.role} onChange={(e) => update("role", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => update("source", v as any)} disabled={lockSource}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => update("stage", v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Situation type</Label>
              <Select value={form.situation_type} onValueChange={(v) => update("situation_type", v as any)}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {SITUATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => update("urgency", v as any)}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {URGENCIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Message *</Label>
            <Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
